import { X } from "lucide-react";
import { useId, useRef, type ReactNode, type RefObject } from "react";
import { Button, type ButtonVariant } from "./Button";
import { Dialog as UiDialog, DialogContent, DialogTitle, DialogDescription, DialogClose } from "./ui/dialog";
import { cn } from "./cn";

export interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg";
  closeLabel?: string;
  contentClassName?: string;
  bodyClassName?: string;
  onCloseAutoFocus?: (event: Event) => void;
  initialFocusRef?: RefObject<HTMLElement | null>;
}
const dialogSizes = { sm: "sm:max-w-md", md: "sm:max-w-xl", lg: "sm:max-w-3xl" };
export function Dialog({ open, onOpenChange, title, description, children, footer, size = "md", closeLabel = "Fechar", contentClassName, bodyClassName, onCloseAutoFocus, initialFocusRef }: DialogProps) {
  const descriptionId = useId();
  const opener = useRef<HTMLElement | null>(null);
  return (
    <UiDialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} aria-describedby={description ? descriptionId : undefined}
        onOpenAutoFocus={(event) => {
          opener.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
          if (initialFocusRef?.current) { event.preventDefault(); initialFocusRef.current.focus(); }
        }}
        onCloseAutoFocus={(event) => {
          if (onCloseAutoFocus) { onCloseAutoFocus(event); return; }
          if (opener.current?.isConnected) { event.preventDefault(); opener.current.focus(); }
        }}
        className={cn("flex max-h-[calc(100dvh-2rem)] flex-col gap-0 overflow-hidden bg-card p-0 shadow-xl", dialogSizes[size], contentClassName)}>
        <header className="flex items-start justify-between gap-4 border-b px-5 py-4">
          <div className="min-w-0">
            <DialogTitle className="text-base leading-6">{title}</DialogTitle>
            {description && <DialogDescription id={descriptionId} className="mt-1 text-xs leading-5">{description}</DialogDescription>}
          </div>
          <DialogClose asChild><Button variant="ghost" size="icon" className="-mr-2 -mt-1 size-8" aria-label={closeLabel}><X className="size-4" aria-hidden="true" /></Button></DialogClose>
        </header>
        <div className={cn("min-h-0 overflow-y-auto p-5", bodyClassName)}>{children}</div>
        {footer && <footer className="flex flex-col-reverse gap-2 border-t bg-background px-5 py-4 sm:flex-row sm:justify-end">{footer}</footer>}
      </DialogContent>
    </UiDialog>
  );
}
export interface ActionDialogProps extends Omit<DialogProps, "footer"> {
  confirmLabel: string;
  cancelLabel?: string;
  confirmVariant?: ButtonVariant;
  isPending?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
}
export function ActionDialog({ confirmLabel, cancelLabel = "Cancelar", confirmVariant = "primary", isPending = false, confirmDisabled = false, onConfirm, onOpenChange, ...props }: ActionDialogProps) {
  return <Dialog {...props} onOpenChange={onOpenChange} footer={<>
    <Button variant="secondary" disabled={isPending} onClick={() => onOpenChange(false)}>{cancelLabel}</Button>
    <Button variant={confirmVariant} onClick={onConfirm} isLoading={isPending} disabled={confirmDisabled} loadingLabel="Processando…">{confirmLabel}</Button>
  </>} />;
}
